import { ChartConfigChartPaneElement, RequireByKey } from '../../types/shared'
import { OverlayType } from '../constants/common'
import { SMACalculation } from '../indicator-calculation/sma'
import Overlay from './overlay'

interface Attrs {
  step: number
  period: string
  maxStep: number
  risingColor: string
  color: string
}

class SMA extends Overlay {
  static type = OverlayType.Sma
  static label = 'Simple Moving Average'

  static getNumOfBarsBuffer(chartConfig: RequireByKey<ChartConfigChartPaneElement, 'period'>) {
    const numOfBarsBuffer = Overlay.getNumOfBarsBuffer(chartConfig)
    return numOfBarsBuffer + 1 // +1 offsets rendering so it doesn't start on first bar which doesn't look OK in SSR rendered charts
  }

  set(obj: Partial<Attrs>) {
    super.set(obj)
    const { period } = obj
    if (typeof period === 'string') {
      this.attrs.period = parseInt(period, 10)
      this.trigger('change')
    }
    return this
  }

  renderContent(context: CanvasRenderingContext2D) {
    super.renderContent()
    const { leftOffset, width } = this.model.chart()
    const period = Math.min(this.attrs.period, this.data.close.length - 1)
    context.beginPath()
    context.translate(0.5, 0.5)
    context.set('lineWidth', 1)
    context.set('strokeStyle', this.attrs.color)

    const smaCalculation = new SMACalculation({
      quote: this.data,
      options: { period },
    })

    smaCalculation.calculate(({ sma }, dataIndex) => {
      const x = this.fx(dataIndex)
      if (this.fx(dataIndex + 1) + leftOffset < 0) {
        return
      }
      if (this.fx(dataIndex - 1) + leftOffset > width) {
        return false
      }
      context.lineTo(x, Math.round(this.fy(sma[dataIndex])))
    })

    context.translate(-0.5, -0.5)
    context.stroke()
  }

  getModalConfig() {
    const period = {
      type: 'number',
      label: 'Period',
      name: 'period',
      value: this.attrs.period ?? 5,
      required: true,
      min: 1,
      max: 999999,
    }
    const color = {
      type: 'color',
      label: 'Color',
      name: 'color',
      value: this.attrs.color ?? this.getFreeColor(),
    }

    return {
      title: this.label,
      inputs: [period, color],
      inputsErrorMessages: {
        period: `${period.label} must be a whole number between ${period.min} and ${period.max}`,
      },
    }
  }

  getIsValid(key: string) {
    switch (key) {
      case 'period':
        return this.getIsNumberInputValid({ key })
      case 'color':
        return !!this.attrs[key] // use real color validation, we got isValidHexColor but it might be also RGBA
      default:
        return false
    }
  }

  toString() {
    return `SMA ${this.attrs.period}`
  }
}

SMA.prototype.defaults = { color: '#ffa75f' }

export default SMA
